// Auth store — Zustand.
//
// accessToken lives in memory only (never in localStorage / sessionStorage).
// The refresh cookie is httpOnly + scoped to /v1/auth — managed by
// payouts-backend, not this store.
//
// `devRole` is a DEV-ONLY UI override exposed by StrataTweaks; when non-null
// it shadows the real role in components that read it. NEVER use devRole for
// authorization decisions — the server is the source of truth.
import { startAuthentication } from "@simplewebauthn/browser";
import { create } from "zustand";

import * as api from "@/api/auth";
import { queryClient } from "@/queryClient";

export type GlobalRole = "admin" | "superadmin" | "user";

/**
 * Membership — one merchant the current user belongs to.
 *
 * display_name + slug + status are joined server-side at /v1/me so the
 * panel can render "Diamond" in the merchant switcher (and elsewhere)
 * instead of bare "Merchant #1". GET /v1/merchants is admin-only, so
 * before this join merchant users had no way to resolve their own
 * merchants' display names.
 *
 * Backwards-compat note: an older backend (pre-join) would return
 * just {merchant_id, role}; the new fields fall through as undefined
 * and the switcher's fallback path renders the legacy label.
 */
export interface Membership {
  merchant_id: number;
  role: string;
  display_name?: string;
  slug?: string;
  status?: string;
  // Per-merchant superadmin API toggle (backend migration 027). When
  // false, the panel hides the "API keys" sidebar entry for this
  // merchant and the merchant's stk_… keys are rejected at
  // BearerDispatch with 401 api_disabled. Optional on the wire for
  // back-compat with pre-027 backends.
  api_enabled?: boolean;
}

export interface Me {
  user_id: number;
  email: string;
  role: GlobalRole;
  memberships: Membership[];
}

export type AuthStatus =
  | "unknown" // first load, hydrating from refresh cookie
  | "anonymous"
  | "authenticated";

interface AuthState {
  accessToken: string | null;
  user: Me | null;
  status: AuthStatus;

  /** DEV-only role override (set by StrataTweaks). null = use real role. */
  devRole: GlobalRole | null;

  /** Set on first /v1/me load. */
  setUser: (m: Me | null) => void;
  setAccessToken: (t: string | null) => void;

  /** OTP flow. */
  requestOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;

  /**
   * Passwordless passkey sign-in. Runs the discoverable WebAuthn ceremony and,
   * on success, follows the same path as verifyOtp (token in memory, refresh
   * cookie set server-side). Throws on failure so the caller can map the error
   * (WebAuthnError → user cancelled).
   */
  loginWithPasskey: () => Promise<void>;

  /** Pull a fresh access token using the refresh cookie. Idempotent. */
  refresh: () => Promise<boolean>;

  /** Hydrate at app boot — attempts a refresh; flips status accordingly. */
  hydrate: () => Promise<void>;

  /** Clear local cookie + state; best-effort upstream logout. */
  logout: () => Promise<void>;

  setDevRole: (r: GlobalRole | null) => void;
}

// Module-scoped in-flight refresh promise. The interceptor in api/client.ts
// fans 401-retries through `refresh()`; on boot multiple queries can enter
// the 401 path simultaneously and would each kick an independent refresh
// against /v1/auth/refresh — a thundering herd that races on cookie rotation
// and produces stale-token state. Dedupe by sharing the in-flight promise.
let refreshInFlight: Promise<boolean> | null = null;

export const useAuth = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  status: "unknown",
  devRole: null,

  setUser: (m) => set({ user: m }),
  setAccessToken: (t) => set({ accessToken: t }),

  requestOtp: async (email) => {
    await api.requestOtp(email);
  },

  verifyOtp: async (email, code) => {
    const { access_token } = await api.verifyOtp(email, code);
    set({ accessToken: access_token, status: "authenticated" });
  },

  loginWithPasskey: async () => {
    const { options, challengeId } = await api.webauthnLoginBegin();
    const credential = await startAuthentication({ optionsJSON: options });
    const { access_token } = await api.webauthnLoginFinish(
      challengeId,
      credential,
    );
    set({ accessToken: access_token, status: "authenticated" });
  },

  refresh: async () => {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const { access_token } = await api.refresh();
        set({ accessToken: access_token, status: "authenticated" });
        return true;
      } catch {
        // AUDIT-PASS-5 T2-A: clear the cached queries before flipping
        // anonymous. The manual sign-out button (App.tsx) calls
        // qc.clear() — but a session that ends via an expired/revoked
        // refresh cookie never hits that path. Without the clear, the
        // previous user's cached ['me'], ['payouts'], ['beneficiaries']
        // queries persist in memory until staleTime + a refetch
        // displaces them. On a shared device the next user can see the
        // previous user's data in the render window.
        queryClient.clear();
        set({ accessToken: null, user: null, status: "anonymous" });
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  },

  hydrate: async () => {
    // If we already have a token in memory, nothing to do.
    if (get().accessToken) {
      set({ status: "authenticated" });
      return;
    }
    const ok = await get().refresh();
    if (!ok) {
      // T2-A: refresh()'s catch branch already cleared the queryClient,
      // but we set status here defensively in case a future change to
      // refresh()'s return-without-throwing path skips the clear.
      set({ status: "anonymous" });
    }
  },

  logout: async () => {
    try {
      await api.logout();
    } catch {
      // ignore — local cleanup is what matters
    } finally {
      // The manual sign-out button (App.tsx) also calls qc.clear()
      // before invoking this. Belt-and-suspenders: clearing here too
      // is a no-op on the manual path, and covers any future caller
      // that forgets the pre-clear.
      queryClient.clear();
      set({ accessToken: null, user: null, status: "anonymous" });
    }
  },

  // AUDIT C-4 hardening: setter is a no-op in production builds so a
  // DevTools-driven `useAuth.getState().setDevRole('admin')` cannot
  // flip the store state at all. effectiveRole() already ignores
  // devRole in prod; this just slams the second door.
  setDevRole: (r) => {
    if (import.meta.env.DEV) set({ devRole: r });
  },
}));

/**
 * effectiveRole returns the role to use for UI gating — devRole if set
 * (dev-only override), otherwise the user's real JWT role.
 *
 * SECURITY: never use this for actual authorization decisions; the server
 * is the source of truth. Only use it to compute UI affordances (sidebar
 * nav variant, "view as" demos, etc.).
 *
 * AUDIT C-4 fix: the devRole branch is gated behind import.meta.env.DEV
 * so Vite eliminates it at build time in production. Pre-fix, the
 * StrataTweaks COMPONENT was already gated, so a normal prod user couldn't
 * flip devRole through the UI — but if anyone poked the Zustand store via
 * DevTools (useAuth.setState({devRole: 'admin'})), the unconditional read
 * here would have surfaced admin nav. That's a UI-only leak (server still
 * enforces), but defence-in-depth says strip the branch entirely.
 */
export function effectiveRole(state: AuthState): GlobalRole | null {
  if (import.meta.env.DEV && state.devRole) return state.devRole;
  return state.user?.role ?? null;
}

/**
 * isAdminRole — true for platform-admin roles. payouts-backend issues "admin"
 * on its own grants; the rest of the platform issues "superadmin". Both unlock
 * the admin UI affordances (admin nav, merchant list, "all merchants" view).
 *
 * SECURITY: UI gating only. The server (auth.IsAdmin) is the authority.
 */
export function isAdminRole(role: GlobalRole | null | undefined): boolean {
  return role === "admin" || role === "superadmin";
}

/**
 * isSuperadminRole — true ONLY for the platform-wide superadmin.
 *
 * Deliberately NOT isAdminRole. Everywhere else in this panel admin and
 * superadmin are interchangeable because superadmin outranks admin; for two
 * surfaces the relationship inverts:
 *
 *   - the Fees screen — what a merchant is CHARGED
 *   - balance source  — which balance a merchant is SHOWN
 *
 * Pricing and disclosure are the platform owner's call, not something every
 * operator who can move money should be able to change. The backend enforces
 * both (auth.RequireSuperadmin, and a per-field check on balance_display);
 * this is the UI half so nobody is shown a control that would 403.
 *
 * SECURITY: UI gating only. The server is the authority.
 */
export function isSuperadminRole(role: GlobalRole | null | undefined): boolean {
  return role === "superadmin";
}
